import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    listRecognitions,
    getRecognitionStats,
    getPrismHealth,
    PrismRecogRow,
    PrismRecogSummary,
    PrismProcSummary,
    RecogListParams,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import Pagination from '../../components/Pagination';
import ImageLightbox from '../../components/ImageLightbox';
import { viewUrl } from '../../utils/imgUrl';
import { toast } from '../../utils/toast';
import { useSyncQueryToUrl } from '../../utils/urlState';
import {
    BarList, DataSourceBar, FunnelBand, NoticeBar, ProcessThumb, RatingBadge, SegGroup,
    StatCard, TIER3, defectText, DEFECT_LABEL } from './parts';
import PrismSyncBar from './PrismSyncBar';
import { usePrismReadLocal } from '../../utils/prismReadLocal';
import { qualityTierCounts } from './quality';
import SceneCard, { SceneDist } from './SceneCard';
import SearchSelect from '../../components/SearchSelect';

/** prism 的 scene 是 snake_case + code, 归一到 SceneCard 的形状 (与加工数据中心同一函数)。 */
function toSceneDist(sc?: {
    labeled: number; evaluable: number; unevaluable: number;
    tags: { code: string; count: number }[];
    unable_reasons: { code: string; count: number }[];
}): SceneDist | undefined {
    if (!sc) return undefined;
    return {
        labeled: sc.labeled, evaluable: sc.evaluable, unevaluable: sc.unevaluable,
        tags: sc.tags.map((t) => ({ key: t.code, count: t.count })),
        unableReasons: sc.unable_reasons.map((r) => ({ key: r.code, count: r.count })),
    };
}

// 数据资产·耗材识别中心 — UI 对齐 studio-admin-console「材料识别」页.
// 2026-09-02 起**不做数据同步**: 后端实时代理 prism-service-api 的
// /dashboard/recognition/* (+「识别之后」段用 /dashboard/processing/summary),
// 前端直接消费 prism 原生字段 (snake_case), 与原平台同一套数据.
// 筛选全部走 prism 源端参数; prism 列表接口无 keyword 参数, 故无关键词搜索.

const PAGE_SIZE = 20;

function pct(n: number, d: number): string {
    return d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—';
}


function RecognitionList() {
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    const [tab, setTab] = useState(initQ.get('tab') === 'table' ? 'table' : 'stats');
    const [rating, setRating] = useState(initQ.get('rating') || '');
    const [official, setOfficial] = useState(initQ.get('official') || '');
    const [hasProcessing, setHasProcessing] = useState(initQ.get('has_processing') || '');
    const [hasSelection, setHasSelection] = useState(initQ.get('has_selection') || '');
    const [linked, setLinked] = useState(initQ.get('linked') || '');
    // 统计口径: 默认「对齐窗口」—— 漏斗只统计第一条加工归档之后的识别 (见 PrismProcSummary.window).
    // 不对齐时上线前那批"根本没法被加工"的识别会混进分母, 后几级转化率被结构性稀释.
    const [aligned, setAligned] = useState(initQ.get('aligned') === 'false' ? '' : 'true');
    const [device, setDevice] = useState(initQ.get('device') || '');
    const [category, setCategory] = useState(initQ.get('category') || '');
    const [uid, setUid] = useState(initQ.get('uid') || '');
    const [uidDraft, setUidDraft] = useState(initQ.get('uid') || '');
    const [defect, setDefect] = useState(initQ.get('processing_defect') || '');
    const [procQuality, setProcQuality] = useState(initQ.get('processing_quality') || '');
    const [procVerdict, setProcVerdict] = useState(initQ.get('processing_verdict') || '');
    const [procScene, setProcScene] = useState(initQ.get('processing_scene') || '');
    // statusSeg 是 has_selection / has_processing 两个源端参数的组合档 (见筛选栏注释)
    const [statusSeg, setStatusSeg] = useState(initQ.get('status') || '');
    const [dateFrom, setDateFrom] = useState(initQ.get('date_from') || '');
    const [dateTo, setDateTo] = useState(initQ.get('date_to') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);

    const [list, setList] = useState<PrismRecogRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState<PrismRecogSummary | null>(null);
    const [procSummary, setProcSummary] = useState<PrismProcSummary | null>(null);
    const [unsupported, setUnsupported] = useState<string[]>([]);
    const [health, setHealth] = useState<{ enabled: boolean; ok?: boolean; error?: string } | null>(null);
    const readLocal = usePrismReadLocal();
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    const [scene, setScene] = useState<SceneDist | undefined>(undefined);
    // 漏斗表格视图 (原型的「表格视图」链接): 三级的数量/占识别/占上一步
    const [funnelTable, setFunnelTable] = useState(false);

    useSyncQueryToUrl('/assets/recognitions', {
        tab: tab === 'stats' ? '' : tab, rating, official, status: statusSeg,
        has_processing: statusSeg ? '' : hasProcessing,
        has_selection: statusSeg ? '' : hasSelection, linked,
        aligned: aligned ? '' : 'false', // 默认对齐, 只把"关掉"写进 URL
        device, category, uid, processing_defect: defect,
        processing_quality: procQuality, processing_verdict: procVerdict, processing_scene: procScene,
        date_from: dateFrom, date_to: dateTo, page,
    });

    // statusParams 把原型的「状态」四分档翻成源端的两个参数; 没选档时沿用下钻设进来的原值.
    const statusParams: { has_processing?: string; has_selection?: string } =
        statusSeg === 'processed' ? { has_processing: 'true' }
            : statusSeg === 'unsaved' ? { has_selection: 'false' }
                : statusSeg === 'saved_unproc' ? { has_selection: 'true', has_processing: 'false' }
                    : { has_processing: hasProcessing || undefined, has_selection: hasSelection || undefined };

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        const params: RecogListParams = {
            rating, official, device, category, uid,
            ...statusParams,
            linked: linked || undefined,
            processing_defect: defect || undefined,
            processing_quality: procQuality || undefined,
            processing_verdict: procVerdict || undefined,
            processing_scene: procScene || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            page, pageSize: PAGE_SIZE,
        };
        try {
            const res = await listRecognitions(params);
            setList(res.data || []);
            setTotal(res.total);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rating, official, device, category, uid, hasProcessing, hasSelection, statusSeg, linked,
        defect, procQuality, procVerdict, procScene, dateFrom, dateTo, page]);

    const loadStats = useCallback(async () => {
        try {
            const s = await getRecognitionStats({
                rating, official, device, category, uid,
                date_from: dateFrom || undefined, date_to: dateTo || undefined,
                aligned: aligned || undefined,
            });
            setSummary(s.summary);
            setProcSummary(s.processingSummary || null);
            setUnsupported(s.unsupportedFilters || []);
            setScene(toSceneDist(s.scene));
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        }
    }, [rating, official, device, category, uid, dateFrom, dateTo, aligned]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadStats(); }, [loadStats]);
    // prism 同步跑完 (手动触发) → 列表与统计一起刷新.
    const onPrismSynced = useCallback(() => { load(); loadStats(); }, [load, loadStats]);
    useEffect(() => { getPrismHealth().then(setHealth).catch(() => undefined); }, []);

    const resetFilters = () => {
        setRating(''); setOfficial(''); setHasProcessing(''); setDevice('');
        setCategory(''); setUid(''); setUidDraft(''); setDefect('');
        setHasSelection(''); setStatusSeg(''); setLinked('');
        setProcQuality(''); setProcVerdict(''); setProcScene('');
        setDateFrom(''); setDateTo(''); setPage(1);
    };
    // 下钻: 整份替换筛选 + 切明细 tab (对齐原平台 onDrill 语义).
    const drill = (patch: {
        rating?: string; hasProcessing?: string; defect?: string;
        hasSelection?: string; linked?: string; procScene?: string;
        procQuality?: string; procVerdict?: string; device?: string; category?: string;
    }) => {
        resetFilters();
        if (patch.rating) setRating(patch.rating);
        if (patch.hasProcessing) setHasProcessing(patch.hasProcessing);
        if (patch.defect) setDefect(patch.defect);
        if (patch.hasSelection) setHasSelection(patch.hasSelection);
        if (patch.linked) setLinked(patch.linked);
        if (patch.procScene) setProcScene(patch.procScene);
        if (patch.procQuality) setProcQuality(patch.procQuality);
        if (patch.procVerdict) setProcVerdict(patch.procVerdict);
        if (patch.device) setDevice(patch.device);
        if (patch.category) setCategory(patch.category);
        setTab('table');
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const k = summary?.kpi;
    const officialFiltered = official !== '';
    const funnel = procSummary?.funnel;
    const win = procSummary?.window;
    const adoption = procSummary?.adoption;
    const effect = procSummary?.effect;
    // 两套口径八键归并 (parts.tsx qualityTierCounts); 「无法判断」不进分母.
    const effectTiers = qualityTierCounts(effect);
    const effectTierBase = effectTiers.base;
    // 加工强度分子必须用「带材料标识的次数」: 源库有大量 material_id 为空/'0' 的存档,
    // 它们不属于任何材料, 拿全量次数除去重材料数是两套 population 相除 (会虚高好几倍).
    const eventsIdentified = funnel?.process_events_identified ?? funnel?.process_events ?? 0;
    const sentinelEvents = (funnel?.process_events ?? 0) - eventsIdentified;
    const intensity = funnel && funnel.processed > 0 ? (eventsIdentified / funnel.processed).toFixed(2) : '—';

    return (
        <div className="space-y-5">
            {/* 页头 (文案对齐原平台) */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Prism · Material</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">材料识别</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        识别量级与质量，识别之后有没有被拿去加工、加工得怎么样。点开任意一条识别任务，能看到它的候选、参数，和后来的每一次加工。
                    </div>
                </div>
                <div className="flex items-start gap-4 shrink-0">
                    {/* 读路径切到本库前仍直连 prism (DataSourceBar), 同步状态条同时显示回填进度 */}
                    {!readLocal && <DataSourceBar health={health} />}
                    <PrismSyncBar onSynced={onPrismSynced} />
                </div>
            </div>

            {/* 唯一的一条筛选栏 (tabs 之上; 全部走 prism 源端参数) */}
            <div className="card px-4 py-3 space-y-2.5">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <SegGroup label="评价" value={rating} onChange={(v) => { setRating(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'good', label: '好评' }, { value: 'bad', label: '差评' }, { value: 'none', label: '无反馈' }]} />
                    <SegGroup label="官方" value={official} onChange={(v) => { setOfficial(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'yes', label: '官方' }, { value: 'no', label: '非官方' }]} />
                    {tab === 'table' && (
                        /* 原型的「状态」四分档, 映射到两个源端参数:
                           未保存      = has_selection=false (识别完没选材料)
                           已保存未加工 = has_selection=true + has_processing=false
                           加工过      = has_processing=true */
                        <SegGroup label="状态" value={statusSeg} onChange={(v) => { setStatusSeg(v); setPage(1); }}
                            options={[
                                { value: '', label: '全部' },
                                { value: 'unsaved', label: '未保存' },
                                { value: 'saved_unproc', label: '已保存未加工' },
                                { value: 'processed', label: '加工过' },
                            ]} />
                    )}
                    <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                        onClick={resetFilters}>清空</button>
                </div>
                {tab === 'table' && (
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <SegGroup label="参数采纳" value={procVerdict} onChange={(v) => { setProcVerdict(v); setPage(1); }}
                            options={[{ value: '', label: '全部' }, { value: 'adopted', label: '推荐参数' },
                                { value: 'adjusted', label: '调整了推荐参数' }, { value: 'no_recommendation', label: '无推荐可比' }]} />
                        <SegGroup label="加工质量" value={procQuality} onChange={(v) => { setProcQuality(v); setPage(1); }}
                            options={[{ value: '', label: '全部' }, { value: 'best,perfect', label: '完美' },
                                { value: 'good,minor_flaw', label: '轻微瑕疵' }, { value: 'acceptable,poor,abnormal', label: '不达标' }, { value: 'unjudgable', label: '无法判断' }]} />
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-slate-50">
                    <SearchSelect className="w-36" value={device} onChange={(v) => { setDevice(v); setPage(1); }}
                        placeholder="全部设备" clearable
                        options={(summary?.by_device || []).filter((d) => d.device).map((d) => ({ value: d.device, label: d.device }))} />
                    <span title="识别候选给出的一级类别 (AI), 不是本平台材料主数据的类别">
                        <SearchSelect className="w-32" value={category} onChange={(v) => { setCategory(v); setPage(1); }}
                            placeholder="全部分类" clearable
                            options={(summary?.by_category || []).filter((d) => d.category).map((d) => ({ value: d.category, label: d.category }))} />
                    </span>
                    <input className="inp" style={{ width: 120 }} placeholder="用户 UID…" value={uidDraft}
                        onChange={(e) => setUidDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setUid(uidDraft.trim()); setPage(1); } }} />
                    {tab === 'table' && (
                        <span title="加工场景 (prism 按加工存档打的标签)">
                            <SearchSelect className="w-36" value={procScene} onChange={(v) => { setProcScene(v); setPage(1); }}
                                placeholder="全部加工场景" clearable
                                options={(scene?.tags || []).map((t) => ({ value: t.key, label: t.key }))} />
                        </span>
                    )}
                    {tab === 'table' && (
                        <SearchSelect className="w-40" value={defect} onChange={(v) => { setDefect(v); setPage(1); }}
                            placeholder="全部缺陷" clearable
                            options={Object.entries(DEFECT_LABEL).map(([code, label]) => ({ value: code, label }))} />
                    )}
                    <input type="date" className="inp" style={{ width: 148 }} value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                    <span className="text-slate-300">~</span>
                    <input type="date" className="inp" style={{ width: 148 }} value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                </div>
            </div>

            {/* tabs (胶囊组, 默认统计) */}
            <div className="flex items-center gap-1">
                {[['stats', '统计'], ['table', '识别明细']].map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'stats' ? (
                <div className="space-y-4">
                    {/* KPI 四张 */}
                    <div className="grid grid-cols-4 gap-4">
                        <StatCard label="总识别量" value={(k?.total ?? 0).toLocaleString()} sub={`已完成 ${(k?.completed ?? 0).toLocaleString()}`} />
                        {/* ⚠️ 这里的「官方」是**识别结果自己标的** candidates[0].is_official (AI 判断),
                            不是"能关联到本平台材料主数据" —— 材料加工的「来源 = 官方材料」才是后者。
                            两页同名不同义, 不能横向对比, 所以在卡上写明。 */}
                        <StatCard label="官方率" value={officialFiltered ? '—' : `${k?.official_ratio ?? 0}%`}
                            sub={officialFiltered ? '已按官方维度筛选，不适用'
                                : `官方命中 ${(k?.official_count ?? 0).toLocaleString()} · 按识别结果自标（非平台主数据关联）`} />
                        <StatCard label="平均置信度" value={k ? String(k.avg_confidence) : '—'} sub="0–100" />
                        <StatCard label="用户反馈" value={((k?.good ?? 0) + (k?.bad ?? 0)).toLocaleString()}
                            sub={`好评 ${k?.good ?? 0} · 差评 ${k?.bad ?? 0}`} />
                    </div>

                    {/* 原型里没有「要看的」问题带, 已移除 —— 差评那批仍可用上方「评价 = 差评」
                        筛到, 认错最多的材料在明细里按材料看。 */}


                    {/* 设备分布 | 材料分类 Top 12 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="设备分布" hint="JS002 已并入 M2 · 点击下钻"
                            rows={(summary?.by_device || []).map((r) => ({
                                label: r.device || '未知', value: r.total,
                                onClick: r.device ? () => drill({ device: r.device }) : undefined,
                            }))} />
                        <BarList title="材料分类 Top 12" hint="取识别候选给出的一级类别 · 点击下钻"
                            note="这是识别结果自带的分类（AI 给的 L1），与材料加工那张按本平台 material.type1 归组的「材料分类」不是同一套词表，别对着看。"
                            rows={(summary?.by_category || []).map((r) => ({
                                label: r.category || '未知', value: r.count,
                                onClick: r.category ? () => drill({ category: r.category }) : undefined,
                            }))} />
                    </div>

                    {/* 识别之后 */}
                    <div className="space-y-3">
                        {unsupported.length > 0 && (
                            <div className="rounded-xl px-4 py-2.5 text-xs leading-relaxed"
                                style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
                                ⚠️ 下面几块只按 <b>设备 / 日期</b> 筛。你还筛了 <b>{unsupported.join(' · ')}</b>，
                                <b>加工侧接口不支持这些维度</b>，所以它们对下面的数字没有影响。
                            </div>
                        )}
                        <div className="flex items-baseline gap-3 flex-wrap">
                            <div className="text-lg font-extrabold text-slate-900">识别之后</div>
                            <div className="text-xs text-slate-400">这 {(funnel?.recognized ?? 0).toLocaleString()} 次识别，最后有多少真被拿去加工。</div>
                            <div className="ml-auto flex items-center gap-2">
                                <SegGroup label="统计口径" value={aligned} onChange={setAligned}
                                    options={[{ value: 'true', label: '对齐窗口' }, { value: '', label: '全部时间' }]} />
                                {win?.archives_since && (
                                    <span className="text-[11px] text-slate-400 font-mono whitespace-nowrap">
                                        {aligned ? `${win.funnel_from || win.archives_since} ~ 至今` : '全部时间'}
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* 口径说明: 加工归档是后上线的能力, 识别早得多 —— 不对齐时那批"上线前根本
                            没法被加工"的识别混在分母里, 后几级转化率被结构性稀释, 不能横向比较。 */}
                        {win?.archives_since && (
                            aligned ? (
                                <div className="rounded-xl px-4 py-2.5 text-xs leading-relaxed"
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}>
                                    「被加工 / 参数采纳 / 加工效果」这几级依赖加工归档，而全库第一条加工归档落在{' '}
                                    <b>{win.archives_since}</b>，识别比它早得多。当前口径下漏斗<b>只统计该日及之后的识别</b>，
                                    四级落在同一可比区间。
                                </div>
                            ) : (
                                <div className="rounded-xl px-4 py-2.5 text-xs leading-relaxed"
                                    style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
                                    ⚠️ <b>口径未对齐。</b>识别含 <b>{win.archives_since}</b> 之前的数据，而「被加工」及之后的指标
                                    从第一条加工归档才开始有。这批「当时无法被加工」的识别混进分母，后几级转化率被结构性稀释，
                                    不能横向比较。
                                </div>
                            )
                        )}

                        {/* 漏斗 + 加工强度 */}
                        <div className="grid gap-4" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
                            <div className="card p-4">
                                <div className="flex items-baseline gap-2 mb-1">
                                    <div className="text-sm font-semibold text-slate-700">转化漏斗</div>
                                    <div className="text-[11px] text-slate-400 ml-auto">识别 → AI 保存 → 被加工</div>
                                </div>
                                <div className="text-[11px] text-slate-400 mb-3">三级都是「识别任务数」，同一单位才好看转化。点任意一级 → 查看该阶段的识别明细。</div>
                                <FunnelBand
                                    levels={[
                                        { label: '识别', value: funnel?.recognized ?? 0, note: '任务数', onClick: () => drill({}) },
                                        {
                                            // 原型叫「AI 保存材料」: 识别完把材料存进素材库那一步,
                                            // 本平台按 user_selection.material_id 数, 语义等同、不跨库.
                                            label: 'AI 保存材料', value: funnel?.selected ?? 0,
                                            // 拆档: 选的材料能不能关联到本平台材料主数据 (候选耗材编码优先, 退回旧库 id)
                                            note: funnel?.selected_official != null
                                                ? `关联平台材料 ${funnel.selected_official.toLocaleString()} · 用户自定义 ${(funnel.selected_custom ?? 0).toLocaleString()}`
                                                : '识别完选了料',
                                            onClick: () => drill({ hasSelection: 'true' }),
                                        },
                                        { label: '被加工材料', value: funnel?.processed_from_recognition ?? 0, onClick: () => drill({ hasProcessing: 'true' }) },
                                    ]}
                                    footer={funnel && funnel.recognized > 0 ? (
                                        <div className="pt-2 mt-1 border-t border-slate-50 text-[11px] text-slate-500 space-y-1">
                                            <div className="flex items-center gap-2">
                                                识别到最终被加工，整体转化
                                                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-semibold">
                                                    {pct(funnel.processed_from_recognition, funnel.recognized)}
                                                </span>
                                                <span className="font-mono">{funnel.recognized.toLocaleString()} → {funnel.processed_from_recognition.toLocaleString()}</span>
                                            </div>
                                            {funnel.selected_official != null && (
                                                <div className="text-slate-500">
                                                    选定的材料里，
                                                    <button type="button" className="text-blue-600 hover:underline"
                                                        onClick={() => drill({ linked: 'official' })}>
                                                        {funnel.selected_official.toLocaleString()} 次关联到平台材料
                                                    </button>
                                                    ，
                                                    <button type="button" className="text-blue-600 hover:underline"
                                                        onClick={() => drill({ linked: 'user_custom' })}>
                                                        {(funnel.selected_custom ?? 0).toLocaleString()} 次是用户自定义
                                                    </button>
                                                    （本平台没有这份材料）
                                                    {funnel.processed_from_recognition_official != null
                                                        ? `；被加工的那批里有 ${funnel.processed_from_recognition_official.toLocaleString()} 次是平台材料。`
                                                        : '。'}
                                                </div>
                                            )}
                                            {aligned && win?.archives_since && (
                                                <div className="text-slate-400">
                                                    对齐口径<b className="font-semibold text-slate-500">只作用于这个漏斗</b>：一级的
                                                    {' '}{funnel.recognized.toLocaleString()} 次是 {win.archives_since} 起的识别，
                                                    上方 KPI「总识别量」仍是全部时间，两个数本就不同。
                                                </div>
                                            )}
                                            <div className="text-slate-400">
                                                右侧「加工强度」与下方各图是<b className="font-semibold text-slate-500">全部加工归档</b>口径（共
                                                {' '}{(funnel.processed ?? 0).toLocaleString()} 种材料），不限于走过识别链路的那批。
                                            </div>
                                        </div>
                                    ) : undefined}
                                />
                                {/* 表格视图 (原型): 三级的数量 / 占识别 / 占上一步 */}
                                <div className="mt-2 pt-2 border-t border-slate-50 text-right">
                                    <button type="button" className="text-[11px] text-blue-600 hover:underline"
                                        onClick={() => setFunnelTable(!funnelTable)}>
                                        {funnelTable ? '收起表格视图' : '表格视图'}
                                    </button>
                                    {funnelTable && funnel && (
                                        <table className="w-full mt-2 text-[11px]">
                                            <thead>
                                                <tr className="text-left text-slate-400 border-b border-slate-100">
                                                    <th className="py-1.5">阶段</th>
                                                    <th className="py-1.5 text-right">数量</th>
                                                    <th className="py-1.5 text-right">占识别</th>
                                                    <th className="py-1.5 text-right">占上一步</th>
                                                </tr>
                                            </thead>
                                            <tbody className="font-mono tabular-nums text-slate-600">
                                                {[
                                                    { name: '识别', v: funnel.recognized, prev: funnel.recognized },
                                                    { name: '用户选定材料', v: funnel.selected, prev: funnel.recognized },
                                                    { name: '选定后被加工', v: funnel.processed_from_recognition, prev: funnel.selected },
                                                ].map((r) => (
                                                    <tr key={r.name} className="border-b border-slate-50">
                                                        <td className="py-1.5 font-sans">{r.name}</td>
                                                        <td className="py-1.5 text-right">{r.v.toLocaleString()}</td>
                                                        <td className="py-1.5 text-right">{pct(r.v, funnel.recognized)}</td>
                                                        <td className="py-1.5 text-right">{pct(r.v, r.prev)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                            <div className="card p-4">
                                <div className="flex items-baseline gap-2 mb-1">
                                    <div className="text-sm font-semibold text-slate-700">加工强度</div>
                                    <div className="text-[11px] text-slate-400 ml-auto">从漏斗里独立出来</div>
                                </div>
                                <div className="text-[11px] text-slate-400">加工次数是「事件数」，不是材料子集，不参与漏斗，单独看。</div>
                                <div className="mt-3 text-[38px] leading-none font-extrabold text-slate-900 tabular-nums">
                                    {intensity}<span className="text-sm font-semibold text-slate-500 ml-1.5">次 / 材料</span>
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1.5">平均每份被加工的材料，被加工 {intensity} 次。</div>
                                <div className="mt-3 pt-3 border-t border-slate-50 space-y-1 text-xs text-slate-600">
                                    <div className="flex"><span className="w-2 h-2 rounded-full bg-indigo-500 inline-block mt-1 mr-2" />加工次数（事件）<span className="ml-auto font-mono">{eventsIdentified.toLocaleString()}</span></div>
                                    <div className="flex"><span className="w-2 h-2 rounded-full bg-indigo-300 inline-block mt-1 mr-2" />被加工材料（去重）<span className="ml-auto font-mono">{(funnel?.processed ?? 0).toLocaleString()}</span></div>
                                </div>
                                {sentinelEvents > 0 && (
                                    <div className="mt-2 text-[11px] text-amber-600 leading-relaxed">
                                        另有 {sentinelEvents.toLocaleString()} 次加工<b>没带材料标识</b>（源库 material_id 为空或 0），
                                        归不到任何材料上，所以不进这个比值，也不在下面按材料的各图里。
                                    </div>
                                )}
                                <div className="mt-3 text-[11px] text-slate-400 bg-slate-50 rounded-lg p-2 leading-relaxed">
                                    <b>为什么拆出来？</b>漏斗每一级都是「材料条数」、层层是子集；而加工次数是一份材料可发生多次的「事件数」。塞进漏斗当第四级会逻辑断层，拆成强度比值后两个问题各自清晰。
                                </div>
                            </div>
                        </div>

                        {/* 参数采纳 | 加工效果 */}
                        <div className="grid grid-cols-2 gap-4">
                            <BarList title="参数采纳" labelWidth="w-36" ordered
                                hint={adoption && adoption.compared > 0 ? `采纳率 ${adoption.adoption_rate}% · 基于被加工材料` : undefined}
                                note="点任意一项 → 跳到识别明细，看这批具体记录"
                                rows={adoption ? [
                                    { label: '推荐参数', value: adoption.adopted, color: '#6366f1', share: pct(adoption.adopted, adoption.adopted + adoption.adjusted), onClick: () => drill({ hasProcessing: 'true' }) },
                                    { label: '调整了推荐参数', value: adoption.adjusted, color: '#a5b4fc', share: pct(adoption.adjusted, adoption.adopted + adoption.adjusted), onClick: () => drill({ hasProcessing: 'true' }) },
                                ] : []}
                                footer={adoption && adoption.unresolved > 0 ? (
                                    <div className="text-[11px] text-amber-600">⚠️ 有 {adoption.unresolved} 行的采纳判定还没回填，它们不落在任何一档里，所以采纳这几个数偏小。</div>
                                ) : undefined} />
                            <BarList title="加工效果" labelWidth="w-44" ordered
                                hint={effect ? `已判定 ${effect.labeled.toLocaleString()} · 待打标 ${effect.unlabeled}` : undefined}
                                note="点任意一项 → 跳到识别明细，看这批具体记录"
                                rows={effect ? [
                                    { label: TIER3.perfect.label, value: effectTiers.perfect, color: TIER3.perfect.bar, share: pct(effectTiers.perfect, effectTierBase), onClick: () => drill({ hasProcessing: 'true' }) },
                                    { label: TIER3.minor.label, value: effectTiers.minor, color: TIER3.minor.bar, share: pct(effectTiers.minor, effectTierBase), onClick: () => drill({ hasProcessing: 'true' }) },
                                    { label: TIER3.fail.label, value: effectTiers.fail, color: TIER3.fail.bar, share: pct(effectTiers.fail, effectTierBase), onClick: () => drill({ hasProcessing: 'true' }) },
                                ] : []}
                                footer={
                                    <div className="space-y-1.5">
                                        {effect && effect.low_confidence > 0 && (
                                            <div className="text-[11px] text-slate-400">另有 {effect.low_confidence} 条图像不可判读（confidence &lt; 0.4），已排除在分布外。</div>
                                        )}
                                        {effect?.top_defects && effect.top_defects.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {effect.top_defects.slice(0, 8).map((t) => (
                                                    <button key={t.code} type="button"
                                                        className="px-1.5 py-0.5 rounded text-[11px]"
                                                        style={{ background: '#fef3c7', color: '#92400e' }}
                                                        onClick={() => drill({ defect: t.code })}>
                                                        {defectText(t.code)} · {t.count}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                } />
                        </div>

                        {/* 加工场景识别 —— **识别链路口径**: 只统计能归因到识别任务的加工存档
                            (后端 recognizedSceneSummary)。材料加工那页同名卡是全量口径,
                            两个数不该互相验算, 各自卡上都写了口径。 */}
                        <SceneCard scene={scene}
                            note="基于识别之后那些加工的结果图（只算能归因到识别任务的存档）。⚠️ 这张卡的单位是**加工存档张数**，不是上面漏斗的识别任务数，两个数不该相互验算。一图可多标签，占比之和大于 100%。点条形 → 明细只看这个场景。"
                            onTagClick={(tag) => drill({ procScene: tag })} />
                    </div>
                </div>
            ) : (
                <>
                    {(hasProcessing || defect || hasSelection || linked) && (
                        <NoticeBar>
                            当前只看
                            {hasProcessing === 'true' && ' 识别后被加工过的'}
                            {hasSelection === 'true' && ' 用户选定了材料的'}
                            {linked === 'official' && ' 选定材料能关联到平台主数据的'}
                            {linked === 'user_custom' && ' 选定材料是用户自定义的'}
                            {defect && ` 加工出「${defectText(defect)}」的`}
                            识别记录（筛选由 prism 源端执行）。
                            <button type="button" className="ml-2 text-blue-600 hover:underline" onClick={resetFilters}>清空</button>
                        </NoticeBar>
                    )}
                    {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}
                    <div className="card overflow-x-auto">
                        <div className="flex items-center px-4 pt-3 pb-2">
                            <div className="text-sm font-semibold text-slate-700">识别明细</div>
                            <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 条</div>
                        </div>
                        <table className="w-full text-[13px]" style={{ minWidth: 1000 }}>
                            <thead>
                                <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                    <th className="px-3 py-2.5 whitespace-nowrap">图片</th>
                                    {/* 描述与材料不给固定宽, 吃掉剩余空间 —— 其余列按内容压到最窄, 整表 1040 即可放下, 常规窗口无需横向滚动 */}
                                    {/* 只有描述列不给宽度 —— 其余列都按内容够放一行来定宽 (设备最长
                                        "F2 Ultra UV 5W"、时间 "2026-09-02 18:26" 都不换行), 剩下的空间
                                        全给描述, 因为它才是需要换行的长文本 */}
                                    {/* 描述与材料给**宽度区间**而不是任其自适应: 这两列多数行是 "—",
                                        table-auto 会按内容把它们压到最窄, 真有内容的行就挤成一条;
                                        故 th 上定下限、单元格内层用 max-w 定上限。 */}
                                    <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 200 }}>描述</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 170 }}>材料</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">用户</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">设备</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">官方</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="识别置信度 0—100">置信</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="用户对这次识别的评价">评价</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="识别之后这份材料被加工的次数">次数</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="最近一次加工的效果判定 (图像不可判读的不算)">加工质量评估</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap" title="加工结果图的场景标签 (prism 按加工存档打, 一图可多标)">加工场景</th>
                                    <th className="px-3 py-2.5 whitespace-nowrap">时间</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={12} className="text-center text-slate-400 py-10">加载中…</td></tr>
                                ) : list.length === 0 ? (
                                    <tr><td colSpan={12} className="text-center text-slate-400 py-10">暂无数据</td></tr>
                                ) : (
                                    list.map((r) => {
                                        const scenes = r.processing_scenes || [];
                                        const isBad = r.rating === 'bad';
                                        return (
                                            <tr key={r.task_id}
                                                className="border-b border-slate-50 hover:bg-blue-50/40 cursor-pointer"
                                                style={isBad ? { boxShadow: 'inset 3px 0 0 #e11d48' } : undefined}
                                                onClick={() => history.push(`/assets/recognitions/${encodeURIComponent(r.task_id)}`)}>
                                                <td className="px-3 py-3">
                                                    {/* 用 ProcessThumb 而不是裸 img: 它把"没有图"与"图渲染不出来"分开占位,
                                                        不会留浏览器的裂图图标 (线上有 3% 的识别图是 HEIC 伪装成 .png, Chrome 解不了)。 */}
                                                    <ProcessThumb src={r.image_url || r.thumb_url} label="识别图" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-3">
                                                    {r.description ? (
                                                        <div className="text-[11px] text-slate-500 break-words max-w-[300px]" title={r.description}
                                                            style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                            {r.description}
                                                        </div>
                                                    ) : '—'}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <div className="text-slate-800 break-words max-w-[240px]" title={r.material_name}
                                                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {r.material_name || '—'}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400">{r.category || '—'}</div>
                                                </td>
                                                <td className="px-3 py-3 font-mono text-[11px] text-slate-500 truncate" title={r.uid || ''}>{r.uid || '—'}</td>
                                                <td className="px-3 py-3">
                                                    <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                                                        {r.device_model}{r.power ? ` ${r.power}W` : ''}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3">
                                                    {r.is_official ? (
                                                        <span className="inline-block px-1.5 py-0.5 rounded-full text-[11px] whitespace-nowrap" style={{ background: '#f5f3ff', color: '#6d28d9' }}>官方</span>
                                                    ) : (
                                                        <span className="inline-block px-1.5 py-0.5 rounded-full text-[11px] whitespace-nowrap border border-slate-200 text-slate-500">非官方</span>
                                                    )}
                                                </td>
                                                <td className={`px-2 py-3 font-mono tabular-nums ${isBad && (r.confidence ?? 0) >= 90 ? 'font-bold text-rose-600' : 'text-slate-600'}`}>
                                                    {r.confidence != null ? r.confidence : '—'}
                                                </td>
                                                <td className="px-3 py-3">
                                                    {isBad ? (
                                                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px]" style={{ background: '#fef2f2', color: '#b91c1c' }}>差评</span>
                                                    ) : (
                                                        <RatingBadge rating={r.rating} />
                                                    )}
                                                </td>
                                                <td className="px-3 py-3">
                                                    {r.processing_count > 0 ? (
                                                        <span className="inline-block px-1.5 py-0.5 rounded-full text-[11px]" style={{ background: '#eef2ff', color: '#4338ca' }}>{r.processing_count}</span>
                                                    ) : (
                                                        <span className="text-slate-400 font-mono">0</span>
                                                    )}
                                                </td>
                                                {/* 加工质量评估: 质量评估链路还没接入, 存量 processing_quality 不是真实判定,
                                                    先统一按"待打标"展示, 等评估结果落库后再还原成 EffectBadge。 */}
                                                <td className="px-3 py-3">
                                                    <span className="text-[11px] text-slate-400">待打标</span>
                                                </td>
                                                <td className="px-3 py-3">
                                                    {scenes.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                            {scenes.map((t) => (
                                                                <span key={t} className="px-1.5 py-0.5 rounded text-[11px]"
                                                                    style={{ background: '#f1f5f9', color: '#475569' }}>{t}</span>
                                                            ))}
                                                        </div>
                                                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                </td>
                                                <td className="px-3 py-3 text-[11px] font-mono text-slate-500 whitespace-nowrap">{formatTime(r.created_at)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
                            <div>{total === 0 ? '0' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`} / {total.toLocaleString()}</div>
                            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                        </div>
                    </div>
                </>
            )}

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default RecognitionList;
